import { MD5 } from "../../share/device/utils.js"
import hmacSHA256 from "crypto-js/hmac-sha256"
import { decryptDes, encryptDes } from "../../share/device/common.js"
import { env } from "@platform/index"
import { callNativeInvoke } from "../../utils/iOSBridge.js"
import { keyboard } from '../../share/device/keyboard.web.js'
import { Logger } from "../../utils/logger.js"


async function getBrowserSN() {
  try {
    const { data } = await callNativeInvoke("get_device_token")
    const { device_token, app_version } = data
    return { device_token, app_version }
  } catch (e) {
    return { device_token: "", app_version: "" }
  }
}

/**
 * @dtsModule max.device
 * @dtsNamespace device
 * @dtsDesc device Device
 */
export const device = {
  /**
   * @dtsExport
   * Restart the device
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.router.restart&lang=%{locale}%)
   */
  restart(): void {
    window.location.reload()
  },
  /**
   * @dtsExport
   * Exit current application
   */
  exit(): void {
    window.close()
  },
  /**
   * @dtsExport
   * Return whether there is a secondary screen
   */
  hasSubScreen(): boolean {
    return false
  },
  /**
   * @dtsExport
   * Get the device SN code
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.getSerialNumber&lang=%{locale}%)
   */
  async getSerialNumber(): Promise<string> {
    try {
      const { device_token, app_version } = await getBrowserSN()
      env.softVersion = app_version
      return device_token
    } catch (e) {
      Logger.error("getSerialNumber Error", e)
      return ""
    }
  },
  /**
   * @dtsExport
   * MD5 encryption
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.md5&lang=%{locale}%)
   */
  md5(str: string): string {
    try {
      if (str == "" || typeof str !== "string")
        throw Error("Parameter must be of string type and cannot be empty.")
      return MD5(str)
    } catch (e) {
      throw Error(e)
    }
  },
  /**
   * @dtsExport
   * SHA256 encryption
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.sha256&lang=%{locale}%)
   */
  sha256(text: string, key: string): string {
    if (
      text == "" ||
      typeof text !== "string" ||
      key == "" ||
      typeof key !== "string"
    )
      throw Error(
        "Parameters 'text' and 'key' must be of type 'string' and cannot be empty."
      )

    return hmacSHA256(text, key)?.toString()
  },
  /**
   * @dtsExport
   * Encrypt the string with DES based on the key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.des&lang=%{locale}%)
   */
  des(text: string, key: string): string {
    if (
      text == "" ||
      typeof text !== "string" ||
      key == "" ||
      typeof key !== "string"
    )
      throw Error(
        "Parameters 'text' and 'key' must be of type 'string' and cannot be empty."
      )
    if (key.length !== 8) throw Error("Parameters 'key' length must be 8 bits.")
    // const reg = new RegExp("^[a-z0-9A-Z]+$");
    // if (!reg.test(key) || key.indexOf("\\") !== -1)
    //   throw Error(
    //     "The parameter 'key' format is wrong! The 'key' must consist of letters or numbers."
    //   );
    return encryptDes(text, key)
  },
  /**
   * @dtsExport
   * Decrypt the string with DES based on the key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.decryptDES&lang=%{locale}%)
   */
  decryptDES(text: string, key: string): string {
    if (
      text == "" ||
      typeof text !== "string" ||
      key == "" ||
      typeof key !== "string"
    )
      throw Error(
        "Parameters 'text' and 'key' must be of type 'string' and cannot be empty."
      )
    if (key.length !== 8) throw Error("Parameters 'key' length must be 8 bits.")
    return decryptDes(text, key)
  },
  /**
   * @dtsExport
   * Encrypt the string with DES based on the key and IV
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.desIV&lang=%{locale}%)
   */
  desIV(text: string, key: string, iv: string): string {
    if (
      text == "" ||
      typeof text !== "string" ||
      key == "" ||
      typeof key !== "string" ||
      iv == "" ||
      typeof iv !== "string"
    )
      throw Error(
        "Parameters 'text','key' and 'iv' must be of type 'string' and cannot be empty."
      )
    if (key.length !== 8 || iv.length !== 8)
      throw Error("Parameters 'key' and 'iv' length must be 8 bits.")
    return encryptDes(text, key, iv)
  },
  /**
   * @dtsExport
   * Decrypt the string with DES based on the key and IV
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.device.decryptDESIV&lang=%{locale}%)
   */
  decryptDESIV(text: string, key: string, iv: string): string {
    if (
      text == "" ||
      typeof text !== "string" ||
      key == "" ||
      typeof key !== "string" ||
      iv == "" ||
      typeof iv !== "string"
    )
      throw Error(
        "Parameters 'text','key' and 'iv' must be of type 'string' and cannot be empty."
      )
    if (key.length !== 8 || iv.length !== 8)
      throw Error("Parameters 'key' and 'iv' length must be 8 bits.")
    return decryptDes(text, key, iv)
  },
  /**
   * @dtsExport
   */
  idleCallback(fn: () => void, options: IdleRequestOptions): void {
    if (typeof fn !== "function") {
      throw Error("idleCallback fn not function")
    }
    requestIdleCallback(fn, options)
  },
  /**
   * @dtsExport
   */
  keyboardRegister(option: { consumed: boolean, onKeyDown: (keyInfo: KeyInfo) => void }): void {
    keyboard.addListener(option)
  },
  /**
   * @dtsExport
   */
  keyboardUnregister(): void {
    keyboard.clearListener()
  },
}
